import {InfoIcon, Pencil, Trash, Utensils} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import {IconButton} from '@/components/ui/icon-button';
import {Separator} from '@/components/ui/separator';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {Typography} from '@/components/ui/typography';
import type {PartOfTheDay} from '@/services/graphqlHooks';
import {
  getPartOfTheDayClasses,
  getPartOfTheDayIcon,
} from '@/shared/tasks/utils/tasksHelpers';
import medicationFoodRelation from '@/translation/medicationFoodRelation';
import partOfTheDay from '@/translation/partOfTheDay';
import unitType from '@/translation/unitType';
import {cn} from '@/utils/ui';

import type {DosageDetailsWithId} from '../utils/medicationTypes';

export type DosageDetailItemProps = DosageDetailsWithId & {
  id: string;
  onEditClick?: (id: string) => void;
  onDeleteClick?: (id: string) => void;
};

const DosageDetailItem = ({
  dosage,
  time,
  timeOfDay,
  relationToFood,
  customInstructions,
  onEditClick,
  onDeleteClick,
  id,
}: DosageDetailItemProps) => {
  const {t} = useTranslation();
  const timeClases = getPartOfTheDayClasses(timeOfDay as PartOfTheDay);

  return (
    <div className={cn('flex flex-col gap-2 border-l border-gray-600')}>
      <Separator className="w-5 translate-y-6" />
      <div className="flex flex-col gap-3 p-1 pl-7">
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {timeOfDay && (
              <TooltipWrapper title={partOfTheDay[timeOfDay]}>
                <div className={timeClases.icon}>
                  {getPartOfTheDayIcon(timeOfDay)}
                </div>
              </TooltipWrapper>
            )}
            {time && <Typography>{time}</Typography>}
          </div>
          {dosage && (
            <Typography>
              {dosage.quantity} {unitType[dosage.unit]}
            </Typography>
          )}
          <Typography>
            {relationToFood && String(relationToFood) !== 'null' && (
              <div className="flex flex-row" style={{gap: 1}}>
                <Utensils size={'18px'} className="mr-2" />
                {medicationFoodRelation[relationToFood]}
              </div>
            )}
          </Typography>
          {(onDeleteClick || onEditClick) && (
            <div className="ml-auto flex gap-1">
              {onEditClick && (
                <TooltipWrapper title={t('Edit')}>
                  <IconButton
                    className="ml-auto"
                    variant="link"
                    onClick={() => {
                      onEditClick(id);
                    }}
                    aria-label={t('Edit')}
                    type="button"
                  >
                    <Pencil />
                    <span className="sr-only">{t('Edit')}</span>
                  </IconButton>
                </TooltipWrapper>
              )}
              {onDeleteClick && (
                <TooltipWrapper title={t('Delete')}>
                  <IconButton
                    className="ml-auto"
                    variant="warning"
                    onClick={() => {
                      onDeleteClick(id);
                    }}
                    aria-label={t('Delete')}
                    type="button"
                  >
                    <Trash />
                    <span className="sr-only">{t('Delete')}</span>
                  </IconButton>
                </TooltipWrapper>
              )}
            </div>
          )}
        </div>
        {customInstructions && (
          <Typography className="inline-flex items-center gap-2 text-sm">
            <InfoIcon size={16} className="shrink-0 text-blue-500" />{' '}
            {customInstructions}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default DosageDetailItem;
